<template>
  <AnalyticsShell>
    <div class="page-heading">
      <div>
        <h1>Overview</h1>
        <p class="page-description">Shipping activity and performance.</p>
      </div>
      <button aria-label="Refresh data" class="refresh-button" type="button" :disabled="loadingAny" @click="refresh">
        <i class="mdi mdi-refresh" aria-hidden="true"></i>{{ loadingAny ? 'Refreshing…' : 'Refresh data' }}
      </button>
    </div>
    <section aria-labelledby="today-heading" class="today-section">
      <div class="section-heading">
        <h2 id="today-heading">
          Today <span class="today-date">{{ today }}</span>
        </h2>
        <span v-if="updated" class="updated" role="status">Updated {{ updated }}</span>
      </div>
      <div v-if="statsError" class="stats-error" role="alert">
        Today's activity could not be loaded. <button type="button" @click="loadStats">Try again</button>
      </div>
      <div class="status-grid" :aria-busy="statsLoading">
        <article v-for="(item, index) in stats" :key="item.title" class="status-card" :class="'status-' + index">
          <div class="status-title">
            <h3>{{ item.title }}</h3>
            <i :class="item.icon" aria-hidden="true"></i>
          </div>
          <strong>{{ statsLoading ? '…' : statsError ? '—' : formatNumber(item.number) }}</strong>
          <p>{{ index === 1 || index === 3 ? 'Shipments up to 90 days old' : 'Today’s activity' }}</p>
        </article>
      </div>
      <p class="status-note">
        <i class="mdi mdi-information-outline" aria-hidden="true"></i>In transit and exception totals exclude shipments
        older than 90 days.
      </p>
    </section>
    <section aria-labelledby="performance-heading" class="performance-section">
      <div class="performance-heading">
        <div>
          <h2 id="performance-heading">Shipping performance</h2>
          <p>Monthly trends <span>·</span> {{ periodLabel }}</p>
        </div>
        <div class="period-control">
          <label for="overview-period">Period</label><select id="overview-period" v-model.number="months" @change="loadCharts">
            <option :value="6">Last 6 months</option>
            <option :value="12">Last 12 months</option>
            <option :value="24">Last 24 months</option>
          </select>
        </div>
      </div>
      <div class="charts-grid">
        <OverviewChart
          v-for="chart in charts"
          :id="'chart-' + chart.key"
          :key="chart.key"
          :title="chart.title"
          :subtitle="chart.subtitle"
          :data="chart.data"
          :unit="chart.unit"
          :stacked="chart.stacked"
          :type="chart.type"
          :summary="chart.key === 'sent' ? 'shipments' : chart.key === 'price' ? 'price' : undefined"
          :loading="chart.loading"
          :error="chart.error"
          @retry="loadCharts"
        />
      </div>
    </section>
  </AnalyticsShell>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import moment from 'moment'
import OverviewChart from './partials/overview-chart.vue'
import Shipments from '@/requests/Shipments'
import AnalyticsShell from './analytics-shell.vue'
import type { Periods } from '@/typings/Periods'
import type { Filter } from '@/typings/Filter'
const request = Shipments()
const today = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
}).format(new Date())
const months = ref(24)
const updated = ref('')
const statsLoading = ref(true)
const statsError = ref(false)
const stats = ref([
  { key: 'created', title: 'Created', number: '', icon: 'mdi mdi-package-variant-closed' },
  { key: 'inTransit', title: 'In transit', number: '', icon: 'mdi mdi-truck-outline' },
  { key: 'delivered', title: 'Delivered', number: '', icon: 'mdi mdi-check-circle-outline' },
  { key: 'exception', title: 'Exception', number: '', icon: 'mdi mdi-alert-circle-outline' },
])
type Chart = {
  key: string
  title: string
  subtitle: string
  unit: string
  stacked: boolean
  type: 'bar' | 'area'
  data: Periods
  loading: boolean
  error: boolean
  // eslint-disable-next-line no-unused-vars
  fetch: (filters: Filter) => Promise<Periods>
}
const empty = (): Periods => ({ currentPeriod: [], previousPeriod: [], periodLabels: [] })
const charts = ref<Chart[]>([
  {
    key: 'sent',
    title: 'Shipments sent',
    subtitle: 'Monthly shipment count',
    unit: 'shipments',
    stacked: false,
    type: 'bar',
    data: empty(),
    loading: true,
    error: false,
    fetch: (f) => request.getShipmentsCreated(f),
  },
  {
    key: 'turnover',
    title: 'Shipping turnover',
    subtitle: 'Monthly revenue by customer',
    unit: 'kr',
    stacked: true,
    type: 'bar',
    data: empty(),
    loading: true,
    error: false,
    fetch: (f) => request.getShipmentsPeriodCustomer(f),
  },
  {
    key: 'volume',
    title: 'Volume',
    subtitle: 'Monthly items by customer',
    unit: 'items',
    stacked: true,
    type: 'bar',
    data: empty(),
    loading: true,
    error: false,
    fetch: (f) => request.getShipmentsVolume(f),
  },
  {
    key: 'price',
    title: 'Average shipping price',
    subtitle: 'Monthly average per shipment',
    unit: 'kr',
    stacked: false,
    type: 'area',
    data: empty(),
    loading: true,
    error: false,
    fetch: (f) => request.getAverageShippingPrice(f),
  },
])
const periodLabel = computed(
  () =>
    `${moment()
      .subtract(months.value - 1, 'months')
      .format('MMM YYYY')} – ${moment().format('MMM YYYY')}`,
)
const loadingAny = computed(() => statsLoading.value || charts.value.some((c) => c.loading))
const formatNumber = (value: string) => {
  const n = Number(value)
  return value !== '' && Number.isFinite(n) ? n.toLocaleString('en-GB') : '—'
}
let chartRequest = 0
async function loadStats() {
  statsLoading.value = true
  statsError.value = false
  try {
    const response = await request.getStatsTodayData({
      period: moment().subtract(29, 'days').format('YYYY-MM-DD') + ' - ' + moment().format('YYYY-MM-DD'),
      customerId: null,
    })
    stats.value.forEach((item) => {
      item.number = response.data.find((d) => d.name === item.key)?.currentPeriod ?? '0'
    })
    updated.value = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(new Date())
  } catch {
    statsError.value = true
  } finally {
    statsLoading.value = false
  }
}
async function loadCharts() {
  const version = ++chartRequest
  const period =
    moment()
      .subtract(months.value - 1, 'months')
      .format('YYYY-MM-01') +
    ' - ' +
    moment().format('YYYY-MM-DD')
  await Promise.all(
    charts.value.map(async (chart) => {
      chart.loading = true
      chart.error = false
      try {
        const data = await chart.fetch({ period, customerId: null, periodGroup: 'month' })
        if (version === chartRequest) chart.data = data
      } catch {
        if (version === chartRequest) chart.error = true
      } finally {
        if (version === chartRequest) chart.loading = false
      }
    }),
  )
}
function refresh() {
  void loadStats()
  void loadCharts()
}
onMounted(refresh)
</script>
<style scoped src="./overview.css"></style>
